/**
 * Short entity entry routes — `/account/:id`, `/contact/:id`, `/:id`.
 *
 * Inbound-only entry points: a rep (or an external system — Salesforce, a
 * Slack link, a bookmark) lands on one of these short URLs, and this module
 * classifies the id and redirects to the canonical Command Center drilldown
 * shape. There are deliberately no outbound link builders for these short
 * forms (plan D4) — every in-app link keeps using the existing canonical
 * builders in `mini-apps/_shared/flow-links.ts`
 * (`accountInCommandCenter`/`contactInCommandCenter`/`contactsInCommandCenter`).
 *
 * Modeled directly on `BareArtifactRoute` (`AppRouter.tsx`) and
 * `legacy-redirects.tsx`: a `useParams` read, a fetch-if-needed, and a
 * `<Navigate replace>`. Unlike `legacy-redirects.tsx`, these do NOT forward
 * the incoming `search`/`hash` (plan D7) — they're new canonical short
 * links with their own query vocabulary (`ContactRedirect` sets `?contact=`
 * itself; unrecognized ids forward to `?q=`).
 */
import { Navigate, useParams } from 'react-router-dom';
import { resolveEntityRoute, type EntityRouteIntent } from '@shared/flow/entity-route';
import {
  accountInCommandCenter,
  contactInCommandCenter,
  searchInCommandCenter,
} from '../../../../../mini-apps/_shared/flow-links';
import { useFlowLookup } from '../search/useFlowLookup';

/** Matches the app's existing spinner markup (`AppRouter.tsx`'s `BareArtifactRoute`). */
function Spinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" />
    </div>
  );
}

export function AccountRedirect({ accountId }: { accountId: string }) {
  return <Navigate to={accountInCommandCenter(accountId)} replace />;
}

export function SearchRedirect({ query }: { query: string }) {
  return <Navigate to={searchInCommandCenter(query)} replace />;
}

/**
 * `/contact/:id` never waits/polls on `resolve_triggered` itself — once the
 * lookup resolves to an account (mirrored or not), this redirects
 * immediately to that account's drilldown with `?contact=` set; the
 * account drilldown's OWN resolving scaffold (`AccountDrillDown.tsx`'s
 * `AccountResolvingScaffold` while polling for an unmirrored account)
 * handles the wait from there. Duplicating that polling here would just be
 * a second, redundant state machine for the same wait.
 */
export function ContactRedirect({ contactId }: { contactId: string }) {
  const lookup = useFlowLookup(contactId, true);

  if (lookup.isLoading) {
    return <Spinner />;
  }

  // Same truthy `account_id` guard GlobalSearch.tsx already uses (kind
  // 'none', a network error, or a malformed/incomplete 'account' payload
  // all fall through to the search fallback rather than crashing or
  // rendering a broken link).
  if (lookup.data?.kind === 'account' && lookup.data.account_id) {
    return <Navigate to={contactInCommandCenter(lookup.data.account_id, contactId)} replace />;
  }

  return <SearchRedirect query={contactId} />;
}

/**
 * Search-fallback query for a typed route whose id did NOT resolve to the
 * entity kind the route promises. When the classifier already said "search",
 * reuse its `query` (the trimmed raw input) rather than re-deriving it; when
 * it resolved to the OTHER entity kind (an Account id under `/contact/`, or
 * vice versa), hand the raw path segment through untouched — never the
 * normalized 18-char form, which would be a mangled id for the wrong object.
 */
function mismatchSearchQuery(intent: EntityRouteIntent, rawId: string): string {
  return intent.kind === 'search' ? intent.query : rawId.trim();
}

/**
 * `/account/:id` and `/contact/:id` promise a specific entity type in their
 * URL shape, so the id is classified through `resolveEntityRoute` exactly like
 * the bare `/:id` route — and only proceeds when the resolved kind MATCHES the
 * route's promise. Anything else (the other entity kind, an email, a domain,
 * an unrecognized word) falls back to the omnibox search seed with the raw id,
 * rather than silently redirecting into a wrong or bogus drilldown state:
 *
 * - `/contact/001…` used to fire a lookup for an Account id and could land on
 *   the account drilldown with the Account's own id in `?contact=` — a filter
 *   bypass no roster row can ever match.
 * - `/account/003…` used to open the account drilldown for a Contact id.
 * - `/contact/jane@acme.co.uk` used to pass a 15-char email straight through
 *   `convertTo18CharId`, which blindly suffixes ANY 15-char string, producing
 *   garbage like `jane@acme.co.ukAAA` instead of a search for the email.
 *
 * 15→18 normalization now happens only inside `resolveEntityRoute`, which
 * gates it on the id actually matching the `001`/`003` SFDC prefix shape.
 */
export function AccountEntryRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/home" replace />;

  const intent = resolveEntityRoute(id);
  if (intent.kind === 'account') return <AccountRedirect accountId={intent.accountId} />;
  return <SearchRedirect query={mismatchSearchQuery(intent, id)} />;
}

export function ContactEntryRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/home" replace />;

  const intent = resolveEntityRoute(id);
  if (intent.kind === 'contact') return <ContactRedirect contactId={intent.contactId} />;
  return <SearchRedirect query={mismatchSearchQuery(intent, id)} />;
}

export function EntityEntryRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/home" replace />;

  const intent = resolveEntityRoute(id);
  switch (intent.kind) {
    case 'account':
      return <AccountRedirect accountId={intent.accountId} />;
    case 'contact':
      return <ContactRedirect contactId={intent.contactId} />;
    case 'search':
      return <SearchRedirect query={intent.query} />;
  }
}
